const AUTH_REFRESH_URL = `${process.env.REACT_APP_URL}/auth/refresh`;
const TOOL_ID = process.env.REACT_APP_TOOL_ID;
const EXPIRY_SKEW_SECONDS = 15;

const parseJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const isAccessTokenExpired = (token: string): boolean => {
  const payload = parseJwtPayload(token);
  const exp = payload?.exp;

  // If token has no exp, treat it as valid and avoid refresh.
  if (typeof exp !== 'number') return false;

  const nowInSeconds = Math.floor(Date.now() / 1000);
  return nowInSeconds >= (exp - EXPIRY_SKEW_SECONDS);
};

/**
 * Refresh access token before protected API calls.
 * Falls back to the currently stored token if refresh fails.
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  const currentAccessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  // Reuse existing access token until it is expired/almost expired.
  if (currentAccessToken && !isAccessTokenExpired(currentAccessToken)) {
    return currentAccessToken;
  }

  // If no refresh token is available, use current access token as-is.
  if (!refreshToken) {
    return currentAccessToken;
  }

  try {
    const response = await fetch(AUTH_REFRESH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refresh_token: refreshToken,
        tool_id: TOOL_ID,
      }),
    });

    // Keep using current token if refresh endpoint is unavailable.
    if (!response.ok) {
      return currentAccessToken;
    }

    const data = await response.json();
    const nextAccessToken =
      data?.access_token ??
      data?.accessToken ??
      data?.data?.access_token ??
      data?.data?.accessToken;

    const nextRefreshToken =
      data?.refresh_token ??
      data?.refreshToken ??
      data?.data?.refresh_token ??
      data?.data?.refreshToken;

    if (nextAccessToken) {
      localStorage.setItem('access_token', String(nextAccessToken));
    }
    if (nextRefreshToken) {
      localStorage.setItem('refresh_token', String(nextRefreshToken));
    }

    return (nextAccessToken ? String(nextAccessToken) : currentAccessToken) ?? null;
  } catch (error) {
    console.error('Error refreshing access token:', error);
    return currentAccessToken;
  }
};

